import React, { useCallback, useState } from 'react'

import { SortableContainer, SortableElement, SortEndHandler } from 'react-sortable-hoc'
import arrayMove from 'array-move'

import { JSONFetcher, formatCents, PopoverIcon } from 'shared'

import { EditButton } from './edit_button'
import { ExternalDonationButton } from './external_donation_button'

import { NeedEditorProps, ListProps, ItemProps, SortableNeedList } from './types'
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}
const Item = (props: ItemProps): JSX.Element => {
  const onDuplicateClicked = useCallback(() => {
    JSONFetcher.post({
      url: props.duplicateUrl,
      success: () => window.location.reload(),
    })
  }, [props.duplicateUrl])
  const onDeleteClicked = useCallback(() => {
    if (window.confirm(I18n.t('misc.are_you_sure_dialogue'))) {
      JSONFetcher.delete({
        url: props.deleteUrl,
        success: () => window.location.reload(),
      })
    }
  }, [props.deleteUrl])
  return (
    <li className="generic-draggable p-2" style={{ display: props.completed ? 'none' : 'block' }}>
      <i className="fa fa-bars grip-handle" />
      <strong>{props.name}</strong>
      <div>{I18n.t('modules.need_editor.still_needed_html', { amount: formatCents(props.stillNeededAmount) })}</div>

      <div className="flex items-center mt-2 mr-12" style={{ flexWrap: 'wrap' }}>
        {props.editable ? (
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {props.editDeUrl && <EditButton locale="de" {...props} />}
            {props.editEnUrl && <EditButton locale="en" {...props} />}
          </div>
        ) : (
          <span className="simulated-link text-gray-700 hover:text-gray-700 whitespace-nowrap desktop:mr-2">
            {I18n.t('modules.need_editor.edit_button')}{' '}
            <PopoverIcon popover={I18n.t('modules.need_editor.edit_disabled_tooltip')} />
          </span>
        )}
        <button className="simulated-link whitespace-nowrap desktop:mr-2 p-0" onClick={onDuplicateClicked}>
          {I18n.t('modules.need_editor.duplicate_button')}
        </button>

        <div className="desktop:mr-2">
          <ExternalDonationButton {...props} />{' '}
          <PopoverIcon popover={I18n.t('modules.need_editor.external_donation_tooltip')} />
        </div>

        {props.deletable && (
          <button
            className="simulated-link text-gray-700 hover:text-gray-700 font-weight-400"
            onClick={onDeleteClicked}
          >
            <i className="fa fa-trash-alt fa-xs mr-1"></i>
            {I18n.t('modules.need_editor.delete_button')}
          </button>
        )}

        {!props.deletable && (
          <span className="text-gray-300 font-weight-400">
            <i className="fa fa-trash-alt fa-xs mr-1"></i>
            <span className="cursor-not-allowed line-through mr-1">{I18n.t('modules.need_editor.delete_button')}</span>
            <PopoverIcon popover={I18n.t('modules.need_editor.delete_disabled_tooltip')} />
          </span>
        )}
      </div>
    </li>
  )
}
const SortableItem = SortableElement(Item)

const List = ({ needs }: ListProps): JSX.Element => {
  return (
    <ol className="list-unstyled">
      {needs.map((need, index) => (
        <SortableItem key={`need-${need.id}`} index={index} {...need} />
      ))}
    </ol>
  )
}
const SortableList = SortableContainer(List)

export const NeedEditor = (props: NeedEditorProps): JSX.Element => {
  const [needs, setNeeds] = useState<SortableNeedList>(JSON.parse(props.needs))
  const onSortEnd: SortEndHandler = useCallback(
    ({ oldIndex, newIndex }) => {
      JSONFetcher.post({
        url: needs[oldIndex].moveUrl,
        body: { position: newIndex },
        success: noop,
        onError: (response) => {
          console.log('sorting failed', response)
        },
      })
      setNeeds(arrayMove(needs, oldIndex, newIndex))
    },
    [needs]
  )

  return <SortableList needs={needs} onSortEnd={onSortEnd} distance={10} lockAxis="y" />
}
